import React, { useEffect, useState } from "react";
import Footer from "../components/layout/Footer";
import Header from "../components/layout/Header";
import { FaAsterisk } from "react-icons/fa";
import authService from "../services/authService";
import { useApiCall } from "../hooks/useAuth";

const labelClassName =
  "mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-200";

const inputClassName =
  "w-full rounded-xl border border-slate-700/80 bg-slate-950/80 px-4 py-3 text-sm text-slate-50 outline-none transition placeholder:text-slate-500 focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20";

const Profile = () => {
  const { loading, error, success, showSuccess, showError, setLoading } = useApiCall();

  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    organization: "",
    job_title: "",
    bio: "",
  });

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        const response = await authService.getProfile();
        if (response.status === "success") {
          const data = response.data;
          setProfile({
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            email: data.email || "",
            phone_number: data.phone_number || "",
            organization: data.organization || "",
            job_title: data.job_title || "",
            bio: data.bio || "",
          });
        }
      } catch (err) {
        showError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [setLoading, showError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authService.updateProfile(profile);
      if (response.status === "success") {
        showSuccess("Profile updated successfully!");
      }
    } catch (err) {
      showError(err.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#05050a] px-4 py-5 sm:px-5 sm:py-7">
        <form
          onSubmit={handleSubmit}
          className="mx-auto w-full max-w-[1100px] rounded-[28px] border border-slate-700/60 bg-[linear-gradient(180deg,rgba(17,24,39,0.96),rgba(15,23,42,0.92))] p-5 shadow-[0_22px_60px_rgba(0,0,0,0.35)] backdrop-blur-[10px] sm:p-8"
        >
          {loading && (
            <div className="mb-5 text-center text-sm text-slate-200">Loading...</div>
          )}

          <div className="mb-6 text-center">
            <h2 className="mt-1 text-2xl font-bold tracking-[-0.02em] text-slate-50 sm:text-[26px]">
              My Profile
            </h2>
            <p className="mt-2 text-sm text-slate-400">
              Manage your personal details and account information.
            </p>
          </div>

          {error && (
            <div className="mb-5 rounded-2xl border border-rose-400/35 bg-rose-500/10 px-4 py-3 text-center text-sm text-rose-100">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-5 rounded-2xl border border-emerald-400/35 bg-emerald-500/10 px-4 py-3 text-center text-sm text-emerald-100">
              {success}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-5 md:gap-y-5">
            <div className="space-y-2">
              <label htmlFor="profile-first-name" className={labelClassName}>
                First Name <FaAsterisk className="mt-0.5 text-[8px] text-rose-400" aria-hidden="true" />
              </label>
              <input
                id="profile-first-name"
                type="text"
                name="first_name"
                value={profile.first_name}
                onChange={handleChange}
                required
                className={inputClassName}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="profile-last-name" className={labelClassName}>
                Last Name
              </label>
              <input
                id="profile-last-name"
                type="text"
                name="last_name"
                value={profile.last_name}
                onChange={handleChange}
                className={inputClassName}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="profile-email" className={labelClassName}>
                Email
              </label>
              <input
                id="profile-email"
                type="email"
                value={profile.email}
                disabled
                className={`${inputClassName} cursor-not-allowed text-slate-300 opacity-90`}
              />
              <small className="block text-xs text-slate-400">Email cannot be changed</small>
            </div>

            <div className="space-y-2">
              <label htmlFor="profile-phone" className={labelClassName}>
                Phone Number
              </label>
              <input
                id="profile-phone"
                type="tel"
                name="phone_number"
                placeholder="Phone Number"
                value={profile.phone_number}
                onChange={handleChange}
                className={inputClassName}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="profile-organization" className={labelClassName}>
                Organization
              </label>
              <input
                id="profile-organization"
                type="text"
                name="organization"
                placeholder="Organization (Optional)"
                value={profile.organization}
                onChange={handleChange}
                className={inputClassName}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="profile-job-title" className={labelClassName}>
                Job Title
              </label>
              <input
                id="profile-job-title"
                type="text"
                name="job_title"
                placeholder="Job Title (Optional)"
                value={profile.job_title}
                onChange={handleChange}
                className={inputClassName}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label htmlFor="profile-bio" className={labelClassName}>
                Bio
              </label>
              <textarea
                id="profile-bio"
                name="bio"
                placeholder="Tell us about yourself..."
                value={profile.bio}
                onChange={handleChange}
                rows={4}
                className={`${inputClassName} min-h-[120px] resize-y`}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-5 w-full rounded-xl bg-blue-600 px-4 py-3.5 text-base font-semibold text-white shadow-[0_10px_24px_rgba(37,99,235,0.28)] transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-slate-600 disabled:shadow-none"
          >
            {loading ? "Updating..." : "Update Profile"}
          </button>
        </form>
      </div>
      <div className="bg-[#0a0e27]">
        <Footer />
      </div>
    </>
  );
};

export default Profile;
